import { AuthContext, Spinner, IAuthContextProps } from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { IInjectedRootStore } from '../../stores';
import { ChartBackground } from './chart-background';
import { ChartLineGroup } from './chart-line-group';
import * as S from './claim-volume-chart.styles';
import { FocusGroup } from './focus-group';
import { IChartTimeline, ITimelineChartProps, ITimelineChartState } from './models';
import { createChartRect, summarizeTimeline, timelineLabels } from './util';
import { XAxis } from './x-axis';
import { YAxis } from './y-axis';

@inject('rootStore')
@observer
export class ClaimVolumeChart extends React.Component<ITimelineChartProps, ITimelineChartState> {
    public static contextType = AuthContext;
    private wrapperRef: React.RefObject<HTMLDivElement>;

    constructor(props: ITimelineChartProps) {
        super(props);
        this.wrapperRef = React.createRef();
        this.state = {
            granularity: undefined,
            mouseX: -1,
            mouseY: -1,
        };
    }
    public componentDidMount(): void {
        window.addEventListener('resize', this.updateChartRect);
        (this.context as any).setGroup('claim-volume');
        this.updateChartRect();
        this.updateChartTimeline();
    }
    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.updateChartRect);
        (this.context as any).cancelGroup('claim-volume');
    }
    public componentDidUpdate(prevProps: ITimelineChartProps): void {
        // Only update if props actually changed
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.granularity !== this.props.granularity ||
            !_.isEqual(prevProps.claimTypes, this.props.claimTypes)
        ) {
            this.updateChartTimeline();
        }
    }
    public render(): React.ReactElement {
        return (
            <S.Wrapper ref={this.wrapperRef}>
                {this.loading ? <Spinner /> : this.error ? this.renderError() : this.renderChart()}
            </S.Wrapper>
        );
    }
    private get injected(): IInjectedRootStore {
        return this.props as ITimelineChartProps & IInjectedRootStore;
    }
    private get loading(): boolean {
        return this.injected.rootStore.metricsStore.claimVolumeTimeline === undefined;
    }
    private get error(): boolean {
        return _.isError(this.injected.rootStore.metricsStore.claimVolumeTimeline);
    }
    private get timeline(): IChartTimeline | undefined {
        return summarizeTimeline(this.injected.rootStore.metricsStore.claimVolumeTimeline, this.state.granularity);
    }
    private renderError = (): React.ReactElement => {
        return <S.Error />;
    };
    private renderChart = (): React.ReactElement | undefined => {
        const { granularity } = this.props;
        const { chartRect } = this.state;
        if (chartRect) {
            const { width, height } = chartRect;
            return (
                <S.Chart
                    width="100%"
                    viewBox={`0 0 ${width} ${height}`}
                    onMouseMove={this.handleMouseMove}
                    onMouseLeave={this.handleMouseLeave}>
                    {this.timeline && chartRect && (
                        <>
                            <ChartBackground chartRect={chartRect} />
                            <XAxis timeline={this.timeline} granularity={granularity} chartRect={chartRect} />
                            <YAxis timeline={this.timeline} granularity={granularity} chartRect={chartRect} />
                            <ChartLineGroup timeline={this.timeline} granularity={granularity} chartRect={chartRect} />
                            {this.renderFocusGroup()}
                        </>
                    )}
                </S.Chart>
            );
        }
    };
    private readonly renderFocusGroup = (): React.ReactElement | undefined => {
        const { granularity } = this.props;
        const { chartRect, mouseX, mouseY } = this.state;
        const labels = timelineLabels.claimVolume;
        if (labels && chartRect && this.timeline && granularity) {
            return (
                <FocusGroup
                    labels={labels}
                    chartRect={chartRect}
                    timeline={this.timeline}
                    granularity={granularity}
                    x={mouseX}
                    y={mouseY}
                />
            );
        }
    };
    private readonly handleMouseMove = (e: React.MouseEvent): void => {
        const { clientRect } = this.state;
        if (clientRect) {
            this.setState({
                mouseX: e.clientX - clientRect.left,
                mouseY: e.nativeEvent.offsetY,
            });
        }
    };
    private readonly handleMouseLeave = (): void => {
        this.setState({
            mouseX: -1,
            mouseY: -1,
        });
    };

    private readonly updateChartTimeline = (): void => {
        if (
            !this.state.granularity ||
            this.state.granularity !== this.props.granularity ||
            (this.timeline && this.timeline.startDate !== this.props.startDate) ||
            (this.timeline && !_.isEqual(this.timeline?.claimTypes, this.props.claimTypes))
        ) {
            this.injected.rootStore.metricsStore.loadClaimVolumeTimeline(
                this.props.startDate,
                this.props.endDate,
                this.props.claimTypes,
                this.injected.rootStore.dateRangeStore.spanTypeForGranularity(this.props.granularity),
            );
            this.setState({
                mouseX: -1,
                mouseY: -1,
                granularity: this.props.granularity,
            });
        }
    };

    private readonly updateChartRect = (): void => {
        if (this.wrapperRef && this.wrapperRef.current) {
            const clientRect = this.wrapperRef.current.getBoundingClientRect();
            const chartRect = createChartRect(clientRect);
            if (!_.isEqual(chartRect, this.state.chartRect)) {
                this.setState({
                    clientRect,
                    chartRect,
                });
            }
        }
    };
}
